import {
  DESIGN_HEIGHT,
  DESIGN_WIDTH,
  frontalQuad,
  horizontalQuad,
  project,
  toPoints,
} from '../geometry';
import { boxFaces, seeded, worldPoints, scope } from './kit';
import { KITCHEN, kitchenCamera as cam, kitchenRegions } from './kitchen.geometry';
import { PAL } from './palette';
import { overlayQuad } from './regions';
import type { ArtProps } from './types';

const { zFront, zWall, counterY, splashTop } = KITCHEN;
const P = (x: number, y: number, z: number) => project(cam, [x, y, z]);

/** A leaf as a closed path from its base, along `angle` degrees (0 = right, -90 = up). */
function leaf(x: number, y: number, angle: number, length: number, width: number): string {
  const rad = (angle * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const tipX = x + dx * length;
  const tipY = y + dy * length;
  const midX = x + dx * length * 0.5;
  const midY = y + dy * length * 0.5;
  const f = (value: number): string => value.toFixed(1);
  const left = `${f(midX - dy * width)} ${f(midY + dx * width)}`;
  const right = `${f(midX + dy * width)} ${f(midY - dx * width)}`;
  return `M${f(x)} ${f(y)}Q${left} ${f(tipX)} ${f(tipY)}Q${right} ${f(x)} ${f(y)}Z`;
}

// ── Back: the room behind the stone ─────────────────────────────────────────────────────────────

export function KitchenBack({ ids }: ArtProps) {
  const { id, ref } = scope(ids, 'back');

  const winOuter = frontalQuad(cam, -0.66, 0.66, 1.62, 2.46, zWall);
  const winGlass = frontalQuad(cam, -0.61, 0.61, 1.67, 2.41, zWall);
  const sillTop = horizontalQuad(cam, -0.72, 0.72, zWall - 0.1, zWall, 1.62);
  const sillFront = frontalQuad(cam, -0.72, 0.72, 1.58, 1.62, zWall - 0.1);
  const midY = P(0, 2.04, zWall)[1];
  const midX = P(0, 2.04, zWall)[0];
  const glassTop = winGlass[0];
  const glassBottom = winGlass[3];
  const glassRight = winGlass[1];

  // lower cabinet run, front plane at the counter edge
  const lowerBottom = 0.1;
  const drawerTop = counterY - 0.045;
  const drawerBottom = 0.67;
  const drawerXs = [-1.95, -0.65, 0.65, 1.95] as const;
  const doorXs = [-1.95, -1.3, -0.65, 0, 0.65, 1.3, 1.95] as const;

  const rng = seeded(11);
  const outside = Array.from({ length: 7 }, () => ({
    x: glassTop[0] + rng() * (glassRight[0] - glassTop[0]),
    y: glassBottom[1] - 10 - rng() * 70,
    rx: 36 + rng() * 40,
    ry: 20 + rng() * 26,
    o: 0.35 + rng() * 0.3,
  }));

  return (
    <g>
      <defs>
        <linearGradient id={id('wall')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={PAL.wallLo} />
          <stop offset="0.55" stopColor={PAL.wall} />
          <stop offset="1" stopColor={PAL.wallHi} />
        </linearGradient>
        <radialGradient
          id={id('spill')}
          cx="600"
          cy="190"
          r="560"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(600 190) scale(1 0.75) translate(-600 -190)"
        >
          <stop offset="0" stopColor={PAL.daylight} stopOpacity="0.28" />
          <stop offset="0.55" stopColor={PAL.daylight} stopOpacity="0.07" />
          <stop offset="1" stopColor={PAL.daylight} stopOpacity="0" />
        </radialGradient>
        <linearGradient
          id={id('glass')}
          x1="0"
          y1={winGlass[0][1]}
          x2="0"
          y2={winGlass[3][1]}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#bcd0dc" />
          <stop offset="0.6" stopColor="#e9f0f2" />
          <stop offset="1" stopColor="#f6efe0" />
        </linearGradient>
        <linearGradient id={id('drawer')} x1="0" y1="0" x2="1" y2="0.3">
          <stop offset="0" stopColor={PAL.cabinetHi} />
          <stop offset="1" stopColor={PAL.cabinet} />
        </linearGradient>
        <linearGradient id={id('brass')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={PAL.brassHi} />
          <stop offset="0.5" stopColor={PAL.brass} />
          <stop offset="1" stopColor={PAL.brassLo} />
        </linearGradient>
        <filter id={id('soft')} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <clipPath id={id('glassClip')}>
          <polygon points={toPoints(winGlass)} />
        </clipPath>
      </defs>

      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill={ref('wall')} />
      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill={ref('spill')} />

      {/* window: steel frame, daylight, a few soft trees outside, one transom */}
      <polygon points={toPoints(winOuter)} fill="#1b1c1f" />
      <polygon points={toPoints(winGlass)} fill={ref('glass')} />
      <g clipPath={`url(#${id('glassClip')})`} filter={`url(#${id('soft')})`}>
        {outside.map((blob, index) => (
          <ellipse
            key={index}
            cx={blob.x}
            cy={blob.y}
            rx={blob.rx}
            ry={blob.ry}
            fill="#5f7a64"
            opacity={blob.o}
          />
        ))}
      </g>
      <line
        x1={midX}
        y1={glassTop[1]}
        x2={midX}
        y2={glassBottom[1]}
        stroke="#1b1c1f"
        strokeWidth="5"
      />
      <line
        x1={glassTop[0]}
        y1={midY}
        x2={glassRight[0]}
        y2={midY}
        stroke="#1b1c1f"
        strokeWidth="5"
      />
      <polygon points={toPoints(sillTop)} fill="#cdc4b4" />
      <polygon points={toPoints(sillFront)} fill="#8f8777" />

      {/* lower cabinets: dark, matte, with brass bar pulls */}
      <polygon
        points={worldPoints(cam, [
          [-1.95, drawerTop + 0.045, zFront],
          [1.95, drawerTop + 0.045, zFront],
          [1.95, lowerBottom, zFront],
          [-1.95, lowerBottom, zFront],
        ])}
        fill={PAL.cabinetLo}
      />
      {([0, 1, 2] as const).map((index) => {
        const x0 = drawerXs[index] ?? 0;
        const x1 = drawerXs[index + 1] ?? 0;
        const face = [
          [x0 + 0.012, drawerTop, zFront],
          [x1 - 0.012, drawerTop, zFront],
          [x1 - 0.012, drawerBottom, zFront],
          [x0 + 0.012, drawerBottom, zFront],
        ] as const;
        const cx = (x0 + x1) / 2;
        const pull = frontalQuad(
          cam,
          cx - 0.17,
          cx + 0.17,
          drawerTop - 0.045,
          drawerTop - 0.033,
          zFront,
        );
        return (
          <g key={index}>
            <polygon points={worldPoints(cam, face)} fill={ref('drawer')} />
            <polyline
              points={worldPoints(cam, [face[3], face[0], face[1]])}
              fill="none"
              stroke={PAL.cabinetEdge}
              strokeWidth="1.5"
              opacity="0.8"
            />
            <polygon points={toPoints(pull)} fill={ref('brass')} />
          </g>
        );
      })}
      {([0, 1, 2, 3, 4, 5] as const).map((index) => {
        const x0 = doorXs[index] ?? 0;
        const x1 = doorXs[index + 1] ?? 0;
        const face = [
          [x0 + 0.01, drawerBottom - 0.015, zFront],
          [x1 - 0.01, drawerBottom - 0.015, zFront],
          [x1 - 0.01, lowerBottom + 0.02, zFront],
          [x0 + 0.01, lowerBottom + 0.02, zFront],
        ] as const;
        return (
          <g key={index}>
            <polygon points={worldPoints(cam, face)} fill={ref('drawer')} opacity="0.9" />
            <polyline
              points={worldPoints(cam, [face[3], face[0], face[1]])}
              fill="none"
              stroke={PAL.cabinetEdge}
              strokeWidth="1.5"
              opacity="0.6"
            />
          </g>
        );
      })}
    </g>
  );
}

// ── Shade: soft shadows multiplied over both the plain and the stone surfaces ───────────────────

export function KitchenShade({ ids }: ArtProps) {
  const { id } = scope(ids, 'shade');
  const [splash, top, edge] = kitchenRegions;
  const backV = P(0, counterY, zWall)[1];
  const frontV = P(0, counterY, zFront)[1];
  const splashTopV = P(0, splashTop, zWall)[1];
  return (
    <g>
      <defs>
        <linearGradient
          id={id('top')}
          x1="0"
          y1={backV}
          x2="0"
          y2={frontV}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0.5" />
          <stop offset="0.16" stopColor="#000" stopOpacity="0.12" />
          <stop offset="0.7" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.2" />
        </linearGradient>
        <linearGradient
          id={id('splash')}
          x1="0"
          y1={splashTopV}
          x2="0"
          y2={backV}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0.5" />
          <stop offset="0.3" stopColor="#000" stopOpacity="0.08" />
          <stop offset="0.8" stopColor="#000" stopOpacity="0.02" />
          <stop offset="1" stopColor="#000" stopOpacity="0.4" />
        </linearGradient>
        <radialGradient
          id={id('vignette')}
          cx="600"
          cy="400"
          r="760"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(600 400) scale(1 0.7) translate(-600 -400)"
        >
          <stop offset="0.45" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.55" />
        </radialGradient>
      </defs>
      {splash ? (
        <polygon points={toPoints(overlayQuad(splash))} fill={`url(#${id('splash')})`} />
      ) : null}
      {top ? <polygon points={toPoints(overlayQuad(top))} fill={`url(#${id('top')})`} /> : null}
      {edge ? <polygon points={toPoints(overlayQuad(edge))} fill="#000" opacity="0.32" /> : null}
      {[splash, top, edge].map((region) =>
        region ? (
          <polygon
            key={region.id}
            points={toPoints(overlayQuad(region))}
            fill={`url(#${id('vignette')})`}
          />
        ) : null,
      )}
    </g>
  );
}

// ── Sheen: highlights of a polished stone (soft-light, clipped to the stone) ────────────────────

export function KitchenSheen({ ids }: ArtProps) {
  const { id } = scope(ids, 'sheen');
  const backV = P(0, counterY, zWall)[1];
  const frontV = P(0, counterY, zFront)[1];
  const splashTopV = P(0, splashTop, zWall)[1];
  const midV = (backV + frontV) / 2;
  return (
    <g>
      <defs>
        <filter id={id('blur-lg')} x="-30%" y="-80%" width="160%" height="260%">
          <feGaussianBlur stdDeviation="16" />
        </filter>
        <filter id={id('blur-sm')} x="-30%" y="-80%" width="160%" height="260%">
          <feGaussianBlur stdDeviation="3.5" />
        </filter>
        <linearGradient
          id={id('led')}
          x1="0"
          y1={splashTopV}
          x2="0"
          y2={splashTopV + 150}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={PAL.warmHot} stopOpacity="0.9" />
          <stop offset="0.35" stopColor={PAL.warm} stopOpacity="0.38" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id('band')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.5" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* under-cabinet strip light washing the backsplash */}
      <rect
        x="0"
        y={splashTopV - 4}
        width={DESIGN_WIDTH}
        height="160"
        fill={`url(#${id('led')})`}
      />
      {/* the window reflected in the counter top */}
      <ellipse
        cx="600"
        cy={midV + 6}
        rx="250"
        ry="32"
        fill="#fff"
        opacity="0.7"
        filter={`url(#${id('blur-lg')})`}
      />
      {/* the two pendants reflected as small warm pools */}
      <ellipse
        cx="424"
        cy={midV + 18}
        rx="34"
        ry="9"
        fill={PAL.warm}
        opacity="0.75"
        filter={`url(#${id('blur-sm')})`}
      />
      <ellipse
        cx="776"
        cy={midV + 18}
        rx="34"
        ry="9"
        fill={PAL.warm}
        opacity="0.75"
        filter={`url(#${id('blur-sm')})`}
      />
      {/* a polished edge catches the light along its whole length */}
      <rect
        x="0"
        y={frontV - 1.5}
        width={DESIGN_WIDTH}
        height="3"
        fill="#fff"
        opacity="0.8"
        filter={`url(#${id('blur-sm')})`}
      />
      {/* a long diagonal sheen across the backsplash */}
      <polygon
        points={`330,${splashTopV} 520,${splashTopV} 330,${backV} 140,${backV}`}
        fill="#fff"
        opacity="0.16"
        filter={`url(#${id('blur-lg')})`}
      />
      <polygon
        points={`820,${splashTopV} 900,${splashTopV} 760,${backV} 680,${backV}`}
        fill="#fff"
        opacity="0.1"
        filter={`url(#${id('blur-lg')})`}
      />
    </g>
  );
}

// ── Front: everything standing in front of the stone ────────────────────────────────────────────

export function KitchenFront({ ids }: ArtProps) {
  const { id, ref } = scope(ids, 'front');

  // upper cabinets either side of the window
  const upperY0 = splashTop;
  const upperY1 = 2.32;
  const left = boxFaces(cam, { x0: -2.8, x1: -0.78, y0: upperY0, y1: upperY1, z0: 0.3, z1: zWall });
  const right = boxFaces(cam, { x0: 0.78, x1: 2.8, y0: upperY0, y1: upperY1, z0: 0.3, z1: zWall });

  // sink
  const sinkRim = horizontalQuad(cam, -0.4, 0.4, 0.1, 0.5, counterY);
  const sinkBottom = horizontalQuad(cam, -0.36, 0.36, 0.14, 0.46, counterY - 0.24);
  const faucetBase = P(0, counterY, 0.6);
  const faucetTop = P(0, 1.3, 0.6);
  const spout = P(0, 1.36, 0.42);
  const spoutTip = P(0, 1.2, 0.34);
  const leverBase = P(0, 1.14, 0.6);
  const lever = P(0.12, 1.2, 0.6);

  // plant, left
  const potBase = P(-1.05, counterY, 0.42);
  const potTopY = P(-1.05, counterY + 0.17, 0.42)[1];
  const potScale = 900 / (0.42 + 1.9);
  const rng = seeded(5);
  const leaves = Array.from({ length: 15 }, (_, index) => {
    const angle = -90 + (index - 7) * 11 + (rng() - 0.5) * 12;
    return {
      d: leaf(
        potBase[0] + (rng() - 0.5) * 12,
        potTopY + 2,
        angle,
        100 + rng() * 80,
        15 + rng() * 9,
      ),
      fill: index % 3 === 0 ? PAL.leafHi : index % 3 === 1 ? PAL.leaf : PAL.leafLo,
    };
  });

  // bowl with fruit, right
  const bowl = P(1.02, counterY, 0.4);
  const bowlW = 0.15 * potScale * 1.9;

  // wooden board leaning on the backsplash, far right
  const board = frontalQuad(cam, 1.2, 1.5, counterY + 0.02, counterY + 0.4, zWall - 0.03);

  // pendants
  const pendants = [-0.46, 0.46].map((x) => {
    const top = P(x, 2.9, 0.25);
    const shadeTop = P(x, 2.02, 0.25);
    const shadeBottom = P(x, 1.9, 0.25);
    const half = 0.15 * (900 / 2.15);
    return { x: shadeTop[0], topY: top[1], y0: shadeTop[1], y1: shadeBottom[1], half };
  });

  return (
    <g>
      <defs>
        <linearGradient id={id('upper')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={PAL.cabinetHi} />
          <stop offset="1" stopColor={PAL.cabinet} />
        </linearGradient>
        <linearGradient id={id('upper-r')} x1="1" y1="0" x2="0" y2="0">
          <stop offset="0" stopColor={PAL.cabinetHi} />
          <stop offset="1" stopColor={PAL.cabinet} />
        </linearGradient>
        <linearGradient id={id('side')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#3b3e45" />
          <stop offset="1" stopColor="#23252a" />
        </linearGradient>
        <linearGradient id={id('basin')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2c2f34" />
          <stop offset="1" stopColor="#0c0d0f" />
        </linearGradient>
        <linearGradient id={id('steel')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#8d939b" />
          <stop offset="1" stopColor="#3c4046" />
        </linearGradient>
        <clipPath id={id('sink')}>
          <polygon points={toPoints(sinkRim)} />
        </clipPath>
        <linearGradient id={id('metal')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={PAL.brassLo} />
          <stop offset="0.45" stopColor={PAL.brassHi} />
          <stop offset="1" stopColor={PAL.brass} />
        </linearGradient>
        <linearGradient id={id('pot')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={PAL.pot} />
          <stop offset="1" stopColor={PAL.potLo} />
        </linearGradient>
        <linearGradient id={id('wood')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={PAL.woodHi} />
          <stop offset="1" stopColor={PAL.woodLo} />
        </linearGradient>
        <radialGradient id={id('glow')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={PAL.warm} stopOpacity="0.85" />
          <stop offset="0.4" stopColor={PAL.warm} stopOpacity="0.25" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id={id('vignette')}
          cx="600"
          cy="400"
          r="780"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(600 400) scale(1 0.72) translate(-600 -400)"
        >
          <stop offset="0.5" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.6" />
        </radialGradient>
        <filter id={id('shadow')} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      {/* upper cabinets */}
      {[
        { faces: left, fill: ref('upper'), handleX: -0.84 },
        { faces: right, fill: ref('upper-r'), handleX: 0.84 },
      ].map(({ faces, fill, handleX }, index) => {
        const handle = frontalQuad(
          cam,
          handleX - 0.008,
          handleX + 0.008,
          upperY0 + 0.08,
          upperY0 + 0.3,
          0.3,
        );
        return (
          <g key={index}>
            {faces.left ? <polygon points={toPoints(faces.left)} fill={ref('side')} /> : null}
            {faces.right ? <polygon points={toPoints(faces.right)} fill={ref('side')} /> : null}
            <polygon points={toPoints(faces.front)} fill={fill} />
            <polyline
              points={toPoints([faces.front[3] ?? [0, 0], faces.front[2] ?? [0, 0]])}
              stroke={PAL.warm}
              strokeOpacity="0.7"
              strokeWidth="1.6"
              fill="none"
            />
            <polygon points={toPoints(handle)} fill={ref('metal')} />
          </g>
        );
      })}

      {/* sink and tap: an under-mounted steel basin seen from above — far wall lit, the rest in shade */}
      <polygon points={toPoints(sinkRim)} fill="#0b0c0e" />
      <g clipPath={`url(#${id('sink')})`}>
        <polygon
          points={toPoints([sinkRim[3], sinkRim[0], sinkBottom[0], sinkBottom[3]])}
          fill="#15171a"
        />
        <polygon
          points={toPoints([sinkRim[1], sinkRim[2], sinkBottom[2], sinkBottom[1]])}
          fill="#101214"
        />
        <polygon points={toPoints(sinkBottom)} fill={ref('basin')} />
        <polygon
          points={toPoints([sinkRim[0], sinkRim[1], sinkBottom[1], sinkBottom[0]])}
          fill={ref('steel')}
        />
        <ellipse
          cx={(sinkBottom[0][0] + sinkBottom[1][0]) / 2}
          cy={(sinkBottom[0][1] + sinkBottom[3][1]) / 2 + 6}
          rx="9"
          ry="3"
          fill="#050506"
        />
      </g>
      <polyline
        points={toPoints([sinkRim[3], sinkRim[0], sinkRim[1], sinkRim[2]])}
        stroke="#9aa0a8"
        strokeOpacity="0.55"
        strokeWidth="1.4"
        fill="none"
      />
      <ellipse
        cx={faucetBase[0]}
        cy={faucetBase[1] + 4}
        rx="16"
        ry="5"
        fill="#000"
        opacity="0.4"
        filter={`url(#${id('shadow')})`}
      />
      <path
        d={`M${faucetBase[0]} ${faucetBase[1]}L${faucetTop[0]} ${faucetTop[1]}C${faucetTop[0]} ${spout[1] - 14} ${spout[0]} ${spout[1] - 10} ${spoutTip[0]} ${spout[1]}L${spoutTip[0]} ${spoutTip[1]}`}
        fill="none"
        stroke={PAL.brassLo}
        strokeWidth="11"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={`M${faucetBase[0]} ${faucetBase[1]}L${faucetTop[0]} ${faucetTop[1]}C${faucetTop[0]} ${spout[1] - 14} ${spout[0]} ${spout[1] - 10} ${spoutTip[0]} ${spout[1]}L${spoutTip[0]} ${spoutTip[1]}`}
        fill="none"
        stroke={ref('metal')}
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={`M${faucetBase[0] - 1.5} ${faucetBase[1] - 4}L${faucetTop[0] - 1.5} ${faucetTop[1]}`}
        fill="none"
        stroke={PAL.brassHi}
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.9"
      />
      <path
        d={`M${leverBase[0]} ${leverBase[1]}L${lever[0]} ${lever[1] - 10}`}
        fill="none"
        stroke={ref('metal')}
        strokeWidth="6"
        strokeLinecap="round"
      />

      {/* plant */}
      <ellipse
        cx={potBase[0] + 6}
        cy={potBase[1] + 3}
        rx="52"
        ry="9"
        fill="#000"
        opacity="0.45"
        filter={`url(#${id('shadow')})`}
      />
      {leaves.map((item, index) => (
        <path key={index} d={item.d} fill={item.fill} />
      ))}
      <path
        d={`M${potBase[0] - 38} ${potTopY}L${potBase[0] + 38} ${potTopY}L${potBase[0] + 29} ${potBase[1]}L${potBase[0] - 29} ${potBase[1]}Z`}
        fill={ref('pot')}
      />
      <ellipse cx={potBase[0]} cy={potTopY} rx="38" ry="6" fill={PAL.potLo} />
      <ellipse cx={potBase[0]} cy={potTopY + 0.5} rx="31" ry="4" fill="#2a2118" />

      {/* bowl and fruit */}
      <ellipse
        cx={bowl[0] + 4}
        cy={bowl[1] + 2}
        rx={bowlW * 0.62}
        ry="9"
        fill="#000"
        opacity="0.45"
        filter={`url(#${id('shadow')})`}
      />
      <circle cx={bowl[0] - 16} cy={bowl[1] - 26} r="15" fill="#c9893f" />
      <circle cx={bowl[0] + 12} cy={bowl[1] - 30} r="16" fill="#8e6b2e" />
      <circle cx={bowl[0] - 2} cy={bowl[1] - 40} r="14" fill="#9c4a33" />
      <path
        d={`M${bowl[0] - bowlW / 2} ${bowl[1] - 28}Q${bowl[0] - bowlW / 2} ${bowl[1]} ${bowl[0]} ${bowl[1]}Q${bowl[0] + bowlW / 2} ${bowl[1]} ${bowl[0] + bowlW / 2} ${bowl[1] - 28}Z`}
        fill={PAL.bone}
      />
      <ellipse cx={bowl[0]} cy={bowl[1] - 28} rx={bowlW / 2} ry="7" fill={PAL.boneDim} />
      <ellipse cx={bowl[0]} cy={bowl[1] - 28} rx={bowlW / 2 - 6} ry="4.5" fill="#6e665a" />

      {/* board */}
      <polygon points={toPoints(board)} fill={ref('wood')} />

      {/* pendants */}
      {pendants.map((pendant, index) => (
        <g key={index}>
          <line
            x1={pendant.x}
            y1="0"
            x2={pendant.x}
            y2={pendant.y0}
            stroke="#0d0d0f"
            strokeWidth="2.5"
          />
          <ellipse
            cx={pendant.x}
            cy={pendant.y1 + 40}
            rx={pendant.half * 1.35}
            ry="62"
            fill={ref('glow')}
          />
          <path
            d={`M${pendant.x - pendant.half} ${pendant.y1}Q${pendant.x - pendant.half} ${pendant.y0 - 8} ${pendant.x} ${pendant.y0 - 8}Q${pendant.x + pendant.half} ${pendant.y0 - 8} ${pendant.x + pendant.half} ${pendant.y1}Z`}
            fill={ref('metal')}
          />
          <ellipse cx={pendant.x} cy={pendant.y1} rx={pendant.half} ry="5" fill={PAL.warm} />
        </g>
      ))}

      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill={ref('vignette')} />
    </g>
  );
}
